import { useState } from 'react';

import professionTrackButtonClick from '@hh.ru/analytics-js-events/build/career_platform/profession/profession_track_button_click';
import { translation, format, TranslatedComponent } from '@hh.ru/front-static-app';
import { GridColumn, GridRow, Title, useBreakpoint } from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';

import EmptyStateCareerTrack from 'src/components/EmptyState/EmptyStateCareerTrack';
import getDataQa from 'src/utils/getDataQa';

import AccordionCareerTrack from 'src/widgets/CareerTrack/AccordionCareerTrack';
import BottomsheetProfession from 'src/widgets/CareerTrack/BottomsheetProfession';
import CardProfession from 'src/widgets/CareerTrack/CardProfession';
import CarouselCourseselPrograms from 'src/widgets/CareerTrack/CarouselCourses';
import GraphCareerTrack from 'src/widgets/CareerTrack/GraphCareerTrack';

import styles from './styles.less';

const TrlKeys = {
    title: 'career-platform.widgets.careerTrack.title',
    description: 'career-platform.widgets.careerTrack.description',
    carouselCoursesTitle: 'career-platform.widgets.careerTrack.carouselCourses.title',
};

const DATA_QA_BLOCK = 'career-track';

const CareerTrack: TranslatedComponent = ({ trls }) => {
    const careerTrack = useSelectorNonNullable((state) => state.widgetCareerTrack);
    const [selectedProfessionIndex, setSelectedProfessionIndex] = useState<number | null>(null);

    const { isXS, isS } = useBreakpoint();
    const isMobile = isXS || isS;

    const targetProfession =
        selectedProfessionIndex !== null ? careerTrack?.targetProfessionList[selectedProfessionIndex] : null;

    if (!careerTrack?.sourceProfession || !careerTrack.targetProfessionList.length) {
        return <EmptyStateCareerTrack />;
    }

    const handleSelectProfession = (index: number | null) => {
        if (typeof index === 'number') {
            const targetProfession = careerTrack.targetProfessionList[index];
            professionTrackButtonClick({
                professionId: targetProfession.profession.id,
                professionName: targetProfession.profession.name,
                salaryFrom: targetProfession.salary.from,
                salaryTo: targetProfession.salary.to,
                streamName: targetProfession.profession.stream.name,
            });
        }
        setSelectedProfessionIndex(index);
    };

    return (
        <section className={styles.container} data-qa={getDataQa(DATA_QA_BLOCK)}>
            {isMobile && (
                <BottomsheetProfession
                    selectedProfessionIndex={selectedProfessionIndex}
                    onClose={() => setSelectedProfessionIndex(null)}
                />
            )}
            <header className={styles.header}>
                <Title
                    Element="h3"
                    size="large"
                    description={trls[TrlKeys.description]}
                    dataQaTitle={getDataQa(DATA_QA_BLOCK, 'title')}
                    dataQaDescription={getDataQa(DATA_QA_BLOCK, 'description')}
                >
                    {trls[TrlKeys.title]}
                </Title>
            </header>
            <GridRow>
                <GridColumn xs={4} s={8} m={8} l={8}>
                    <div className={styles.content}>
                        {isMobile && <AccordionCareerTrack />}
                        <GraphCareerTrack
                            selectedProfessionIndex={selectedProfessionIndex}
                            onSelectProfession={handleSelectProfession}
                        />
                    </div>
                </GridColumn>
                <GridColumn xs={0} s={0} m={4} l={4}>
                    <aside className={styles.sidebar}>
                        {!isMobile && <AccordionCareerTrack />}
                        <CardProfession selectedProfessionIndex={selectedProfessionIndex} />
                    </aside>
                </GridColumn>
            </GridRow>
            {!isMobile && targetProfession && !!targetProfession.courses.length && (
                <div>
                    <div className={styles.carouselCoursesTitle}>
                        <Title Element="h4" size="medium">
                            {format(trls[TrlKeys.carouselCoursesTitle], {
                                '{0}': targetProfession.profession.name,
                            })}
                        </Title>
                    </div>
                    <CarouselCourseselPrograms edProgram={targetProfession.courses} />
                </div>
            )}
        </section>
    );
};

export default translation(CareerTrack);
